/**
 * @file ConfirmModal.tsx
 *
 * Confirmation dialog for destructive or primary actions, built on Gravity's
 * {@link StandardDialog}. All copy is passed in already-localized so the
 * component stays presentational. While `loading`, both buttons disable and the
 * confirm button shows `loadingLabel`; an optional `errorMessage` surfaces a
 * failed action without closing the dialog.
 */

import type { ReactNode } from 'react';
import { StandardDialog } from '@group.one/react-gravity';

type ConfirmVariant = 'destructive' | 'primary';

interface ConfirmModalProps {
  title: string;
  message: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  loadingLabel: string;
  confirmVariant?: ConfirmVariant;
  blockDismissWhenLoading?: boolean;
  loading?: boolean;
  /** Optional error shown in-dialog when the action fails. */
  errorMessage?: string;
  onPrimaryClick: () => void;
  onSecondaryClick: () => void;
  onClose: () => void;
}

/**
 * Renders a two-button confirmation dialog.
 *
 * @param props - Localized copy, variant, loading/error state, and handlers.
 *
 * @returns The dialog element.
 */
export function ConfirmModal({
  title,
  message,
  confirmLabel,
  cancelLabel,
  loadingLabel,
  confirmVariant = 'destructive',
  blockDismissWhenLoading = false,
  loading = false,
  errorMessage,
  onPrimaryClick,
  onSecondaryClick,
  onClose,
}: ConfirmModalProps) {
  const handleDismiss = (): void => {
    if (loading && blockDismissWhenLoading) {
      return;
    }
    onClose();
  };

  const confirmBtnClass =
    confirmVariant === 'primary'
      ? 'gv-button gv-button-primary'
      : 'gv-button gv-button-destructive';

  return (
    <StandardDialog
      isOpen
      title={title}
      onClose={handleDismiss}
      onClickAway={handleDismiss}
      onEscape={handleDismiss}
      footer={
        <div className="gv-button-group">
          <button
            className="gv-button gv-button-cancel"
            type="button"
            data-testid="confirm-modal-cancel"
            onClick={onSecondaryClick}
            disabled={loading}
          >
            {cancelLabel}
          </button>
          <button
            className={confirmBtnClass}
            type="button"
            data-testid="confirm-modal-confirm"
            onClick={onPrimaryClick}
            disabled={loading}
          >
            {loading ? loadingLabel : confirmLabel}
          </button>
        </div>
      }
    >
      <div data-testid="confirm-modal">
        <p style={{ margin: 0 }}>{message}</p>
        {errorMessage && (
          <p
            className="gv-text-sm gv-state-critical"
            role="alert"
            data-testid="confirm-modal-error"
            style={{ marginBottom: 0 }}
          >
            {errorMessage}
          </p>
        )}
      </div>
    </StandardDialog>
  );
}
